import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Crown, Shield, Eye, Loader2, Check, AlertTriangle, Mail, Globe, Lock, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  admin:  { Icon: Crown,  label: "Admin" },
  agent:  { Icon: Shield, label: "Agent" },
  viewer: { Icon: Eye,    label: "Viewer" },
};

const VIS_OPTIONS = [
  { id: "private",     Icon: Lock,   label: "Private",     blurb: "Only you can see your messages. Admin needs your explicit consent for oversight." },
  { id: "hybrid",      Icon: Layers, label: "Hybrid",      blurb: "Your messages are yours; agency admin can see all threads for oversight. Colleagues cannot." },
  { id: "transparent", Icon: Globe,  label: "Transparent", blurb: "All messages visible to every workspace member — full team collaboration." },
];

export default function InvitationLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invitation: null, error: null });
  const [accepting, setAccepting] = useState(false);
  // step: 'view' → 'visibility' (after accept) → 'done'
  const [step, setStep] = useState("view");
  const [acceptedWsId, setAcceptedWsId] = useState(null);
  const [acceptedWsName, setAcceptedWsName] = useState(null);
  const [recommended, setRecommended] = useState("hybrid");
  const [visMode, setVisMode] = useState("private");
  const [visSaving, setVisSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invitations/${token}`)
      .then((r) => setState({ loading: false, invitation: r.data, error: null }))
      .catch((e) => setState({
        loading: false, invitation: null,
        error: e?.response?.data?.detail || "Invitation not found or expired.",
      }));
  }, [token]);

  const authToken = (() => {
    try { return localStorage.getItem("pm_token"); } catch { return null; }
  })();

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API}/invitations/${token}/accept`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      try { localStorage.setItem("pm_active_workspace_id", data.workspace_id); } catch (e) { /* noop */ }
      setAcceptedWsId(data.workspace_id);
      setAcceptedWsName(state.invitation?.workspace_name);
      // Pull the workspace's recommended default to pre-select the picker
      try {
        const me = await axios.get(`${API}/me/inbox-visibility?workspace_id=${data.workspace_id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setRecommended(me.data?.recommended || "hybrid");
        setVisMode(me.data?.recommended || "hybrid");
      } catch (e) { /* noop */ }
      setStep("visibility");
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Could not accept invitation" }));
    } finally {
      setAccepting(false);
    }
  };

  const saveVisibility = async () => {
    setVisSaving(true);
    try {
      await axios.put(`${API}/me/inbox-visibility`, { mode: visMode, workspace_id: acceptedWsId }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      navigate("/dashboard");
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Could not save inbox visibility" }));
    } finally {
      setVisSaving(false);
    }
  };

  if (state.loading) {
    return (
      <Frame>
        <div className="text-center text-slate-500 flex items-center gap-2 justify-center"><Loader2 size={14} className="animate-spin" /> Loading invitation…</div>
      </Frame>
    );
  }
  if (state.error || !state.invitation) {
    return (
      <Frame>
        <div className="text-center space-y-3">
          <AlertTriangle size={28} className="text-rose-600 mx-auto" />
          <div className="text-base font-bold text-slate-900">Invitation not available</div>
          <div className="text-sm text-slate-500">{state.error}</div>
          <Link to="/login" className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold inline-flex items-center gap-1.5">Go to Playmaker</Link>
        </div>
      </Frame>
    );
  }

  // ── Step 2: inbox visibility picker (post-accept) ──
  if (step === "visibility") {
    return (
      <Frame>
        <div className="text-center mb-4">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center mb-3">
            <Globe size={18} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Inbox visibility</div>
          <div className="font-display text-xl font-black tracking-tight text-slate-900 mt-1">
            How should your inbox work within {acceptedWsName}?
          </div>
          <div className="text-[12px] text-slate-500 mt-2 max-w-sm mx-auto leading-snug">
            The agency recommends <b className="text-slate-800 capitalize">{recommended}</b>. You can choose any setting — your choice is final and protected under GDPR.
          </div>
        </div>
        <div className="space-y-2.5" data-testid="invite-visibility-picker">
          {VIS_OPTIONS.map((o) => {
            const Icon = o.Icon;
            const isActive = visMode === o.id;
            const isRec = recommended === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setVisMode(o.id)}
                data-testid={`invite-vis-${o.id}`}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all relative ${
                  isActive ? "border-emerald-500 bg-emerald-50/40 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                {isRec && (
                  <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold uppercase tracking-wider">
                    Recommended
                  </span>
                )}
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-bold">{o.label}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5 leading-tight">{o.blurb}</div>
                  </div>
                  {isActive && <Check size={14} className="text-emerald-700 shrink-0 mt-1.5" />}
                </div>
              </button>
            );
          })}
        </div>
        <button
          onClick={saveVisibility}
          disabled={visSaving}
          data-testid="invite-vis-confirm"
          className="pm-btn-primary w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50 mt-4"
        >
          {visSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Confirm my choice & enter workspace
        </button>
        <p className="text-[10px] text-slate-400 mt-3 text-center">
          You can change this anytime from Settings → Privacy. Your choice is logged for compliance.
        </p>
      </Frame>
    );
  }

  // ── Step 1: invitation overview ──
  const inv = state.invitation;
  const Role = ROLE_META[inv.role] || ROLE_META.agent;
  return (
    <Frame>
      <div className="text-center mb-5">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
          <Mail size={18} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Workspace invitation</div>
        <div className="font-display text-2xl font-black tracking-tight text-slate-900 mt-1">
          Join {inv.workspace_name}
        </div>
        <div className="text-sm text-slate-600 mt-1.5">
          <b>{inv.inviter_name}</b> invited <b>{inv.email}</b> as <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-800 text-[12px] font-bold border border-emerald-200"><Role.Icon size={10} /> {Role.label}</span>.
        </div>
      </div>

      {authToken ? (
        <button
          onClick={accept}
          disabled={accepting}
          data-testid="accept-invitation-btn"
          className="pm-btn-primary w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {accepting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {accepting ? "Joining…" : `Accept & join ${inv.workspace_name}`}
        </button>
      ) : (
        <div className="space-y-2">
          <Link
            to={`/login?next=/invite/${token}`}
            data-testid="login-to-accept-btn"
            className="pm-btn-primary w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2"
          >
            Log in to accept
          </Link>
          <Link
            to={`/register?email=${encodeURIComponent(inv.email)}&next=/invite/${token}`}
            data-testid="register-to-accept-btn"
            className="pm-btn-secondary w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center"
          >
            Create your Playmaker account
          </Link>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4 text-center">
        Make sure you're logged in as <b>{inv.email}</b>. This invitation expires {new Date(inv.expires_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}.
      </p>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-emerald-50/30">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-7">
        {children}
      </div>
    </div>
  );
}
